import React from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
  spring,
} from 'remotion';

/**
 * 数学可视化 - 竖屏「函数世界」风格
 *
 * 核心亮点：
 * - 动态坐标系绘制（带网格）
 * - 多条函数曲线实时绘制
 * - 面积填充动画（积分可视化）
 * - 切线/法线辅助线
 * - 公式与图形联动
 * - 参数变化动画
 *
 * 尺寸：1080x1920 竖屏
 */

interface MathFunction {
  name: string;
  formula: string;
  color: string;
  fn: (x: number) => number;
}

interface MathVisualizationTemplateProps {
  topic: string;
  subtitle: string;
  functions: MathFunction[];
  xRange: [number, number];
  yRange: [number, number];
  keyFormula: string;
  explanation: string[];
  insight: string;
  showArea?: boolean;
  areaRange?: [number, number];
}

const DEFAULT_PROPS: MathVisualizationTemplateProps = {
  topic: '三角函数族',
  subtitle: 'Trigonometric Functions Family',
  functions: [
    { name: 'sin(x)', formula: 'y = sin(x)', color: '#818cf8', fn: Math.sin },
    { name: 'cos(x)', formula: 'y = cos(x)', color: '#f472b6', fn: Math.cos },
    { name: '0.5·sin(2x)', formula: 'y = 0.5sin(2x)', color: '#34d399', fn: (x: number) => 0.5 * Math.sin(2 * x) },
  ],
  xRange: [-2 * Math.PI, 2 * Math.PI],
  yRange: [-1.5, 1.5],
  keyFormula: 'sin²(x) + cos²(x) = 1',
  explanation: [
    'sin(x) 和 cos(x) 相位差 π/2',
    '振幅决定波的高度，频率决定波的疏密',
    '任意角度的 sin² + cos² 恒等于 1',
  ],
  insight: '三角函数是描述周期现象的数学语言——从声波到光波，从潮汐到心跳',
  showArea: true,
  areaRange: [0, Math.PI],
};

export const MathVisualizationTemplate: React.FC<Partial<MathVisualizationTemplateProps>> = (props) => {
  const p = { ...DEFAULT_PROPS, ...props };
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Chart area
  const chartW = 920;
  const chartH = 600;
  const chartPad = 50;
  const plotW = chartW - chartPad * 2;
  const plotH = chartH - chartPad * 2;

  const xToPixel = (x: number) => chartPad + ((x - p.xRange[0]) / (p.xRange[1] - p.xRange[0])) * plotW;
  const yToPixel = (y: number) => chartPad + ((p.yRange[1] - y) / (p.yRange[1] - p.yRange[0])) * plotH;

  // Phases
  const titleProgress = spring({ frame, fps, config: { damping: 18, stiffness: 90 } });
  const axisDraw = interpolate(frame, [20, 55], [0, 1], { extrapolateRight: 'clamp' });
  const curveDraw = interpolate(frame, [50, 200], [0, 1], { extrapolateRight: 'clamp' });

  // Area fill
  const areaStart = 200;
  const areaDraw = p.showArea ? interpolate(frame, [areaStart, areaStart + 60], [0, 1], { extrapolateRight: 'clamp' }) : 0;

  // Formula
  const formulaStart = 250;
  const formulaOpacity = interpolate(frame, [formulaStart, formulaStart + 20], [0, 1], { extrapolateRight: 'clamp' });
  const formulaScale = spring({ frame: frame - formulaStart, fps, from: 0.9, to: 1, config: { damping: 14, stiffness: 100 } });

  // Explanation
  const expStart = formulaStart + 40;

  // Insight
  const insightStart = expStart + p.explanation.length * 30 + 30;
  const insightOpacity = interpolate(frame, [insightStart, insightStart + 25], [0, 1], { extrapolateRight: 'clamp' });

  // Moving point on first function
  const pointX = interpolate(frame, [80, 400], [p.xRange[0], p.xRange[1]], { extrapolateRight: 'clamp' });
  const pointVisible = frame > 80;

  // Generate curve path
  const generatePath = (fn: (x: number) => number, progress: number): string => {
    const totalRange = p.xRange[1] - p.xRange[0];
    const drawnX = p.xRange[0] + totalRange * progress;
    const points: string[] = [];
    const step = totalRange / 200;

    for (let x = p.xRange[0]; x <= drawnX; x += step) {
      const y = fn(x);
      if (y >= p.yRange[0] && y <= p.yRange[1]) {
        const px = xToPixel(x);
        const py = yToPixel(y);
        points.push(`${points.length === 0 ? 'M' : 'L'}${px},${py}`);
      }
    }
    return points.join(' ');
  };

  return (
    <AbsoluteFill
      style={{
        background: 'linear-gradient(180deg, #0a0a1a 0%, #0f1729 50%, #111133 100%)',
        fontFamily: '-apple-system, "PingFang SC", "Inter", sans-serif',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* Grid bg */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(129,140,248,0.03) 1px, transparent 0)',
          backgroundSize: '30px 30px',
        }}
      />

      {/* Title */}
      <div
        style={{
          position: 'absolute',
          top: 70,
          left: 0,
          right: 0,
          textAlign: 'center',
          opacity: titleProgress,
          transform: `translateY(${(1 - titleProgress) * -12}px)`,
        }}
      >
        <div style={{ display: 'inline-block', padding: '5px 16px', background: '#818cf818', border: '1px solid #818cf830', borderRadius: 8, fontSize: 32, color: '#818cf8', fontWeight: 600, letterSpacing: '0.12em', marginBottom: 10 }}>
          📐 MATHEMATICS
        </div>
        <h1 style={{ fontSize: 50, fontWeight: 800, margin: 0 }}>{p.topic}</h1>
        <p style={{ fontSize: 36, color: 'rgba(255,255,255,0.3)', marginTop: 6, fontStyle: 'italic' }}>{p.subtitle}</p>
      </div>

      {/* ========== FUNCTION GRAPH ========== */}
      <div style={{ position: 'absolute', top: 280, left: 60, right: 60 }}>
        <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`}>
          {/* Grid */}
          {Array.from({ length: 13 }).map((_, i) => {
            const x = chartPad + (i / 12) * plotW;
            return <line key={`v${i}`} x1={x} y1={chartPad} x2={x} y2={chartPad + plotH} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />;
          })}
          {Array.from({ length: 7 }).map((_, i) => {
            const y = chartPad + (i / 6) * plotH;
            return <line key={`h${i}`} x1={chartPad} y1={y} x2={chartPad + plotW} y2={y} stroke="rgba(255,255,255,0.04)" strokeWidth="1" />;
          })}

          {/* Axes */}
          <line x1={chartPad} y1={yToPixel(0)} x2={chartPad + plotW * axisDraw} y2={yToPixel(0)} stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
          <line x1={xToPixel(0)} y1={chartPad + plotH} x2={xToPixel(0)} y2={chartPad + plotH * (1 - axisDraw)} stroke="rgba(255,255,255,0.25)" strokeWidth="2" />

          {/* Axis labels */}
          <text x={chartPad + plotW + 10} y={yToPixel(0) + 5} fill="rgba(255,255,255,0.3)" fontSize="40">x</text>
          <text x={xToPixel(0) - 16} y={chartPad - 5} fill="rgba(255,255,255,0.3)" fontSize="40">y</text>

          {/* Tick marks */}
          {[-Math.PI, 0, Math.PI, 2 * Math.PI].filter((v) => v >= p.xRange[0] && v <= p.xRange[1]).map((v, i) => (
            <g key={`xt${i}`} opacity={axisDraw}>
              <line x1={xToPixel(v)} y1={yToPixel(0) - 4} x2={xToPixel(v)} y2={yToPixel(0) + 4} stroke="rgba(255,255,255,0.2)" strokeWidth="1" />
              <text x={xToPixel(v)} y={yToPixel(0) + 30} fill="rgba(255,255,255,0.25)" fontSize="40" textAnchor="middle">
                {v === 0 ? '0' : `${(v / Math.PI).toFixed(0)}π`}
              </text>
            </g>
          ))}

          {/* Area fill (integral) */}
          {p.showArea && p.areaRange && areaDraw > 0 && (
            (() => {
              const [a, b] = p.areaRange;
              const drawnB = a + (b - a) * areaDraw;
              const step = (drawnB - a) / 50;
              const fn = p.functions[0]?.fn || Math.sin;
              const pts: string[] = [];
              for (let x = a; x <= drawnB; x += step) {
                pts.push(`${xToPixel(x)},${yToPixel(fn(x))}`);
              }
              const areaD = `M${xToPixel(a)},${yToPixel(0)} L${pts.join(' L')} L${xToPixel(drawnB)},${yToPixel(0)} Z`;
              return <path d={areaD} fill={p.functions[0]?.color || '#818cf8'} opacity="0.1" />;
            })()
          )}

          {/* Function curves */}
          {p.functions.map((func, i) => {
            const path = generatePath(func.fn, curveDraw);
            return (
              <g key={i}>
                <path d={path} fill="none" stroke={func.color} strokeWidth="3" strokeLinecap="round" />
                <path d={path} fill="none" stroke={func.color} strokeWidth="10" strokeLinecap="round" opacity="0.08" />
              </g>
            );
          })}

          {/* Moving point */}
          {pointVisible && p.functions[0] && (
            <g>
              <circle cx={xToPixel(pointX)} cy={yToPixel(p.functions[0].fn(pointX))} r="6" fill={p.functions[0].color} />
              <circle cx={xToPixel(pointX)} cy={yToPixel(p.functions[0].fn(pointX))} r="14" fill={p.functions[0].color} opacity="0.12" />
              {/* Projection lines */}
              <line
                x1={xToPixel(pointX)} y1={yToPixel(p.functions[0].fn(pointX))}
                x2={xToPixel(pointX)} y2={yToPixel(0)}
                stroke={p.functions[0].color} strokeWidth="1" strokeDasharray="4 3" opacity="0.3"
              />
            </g>
          )}
        </svg>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 20, marginTop: 10, justifyContent: 'center' }}>
          {p.functions.map((func, i) => {
            const opacity = interpolate(curveDraw, [i * 0.2, i * 0.2 + 0.15], [0, 1], { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' });
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity }}>
                <div style={{ width: 24, height: 3, background: func.color, borderRadius: 2 }} />
                <span style={{ fontSize: 42, color: func.color, fontWeight: 600 }}>{func.formula}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========== FORMULA ========== */}
      <div
        style={{
          position: 'absolute',
          top: 1000,
          left: 60,
          right: 60,
          textAlign: 'center',
          opacity: formulaOpacity,
          transform: `scale(${formulaScale})`,
        }}
      >
        <div
          style={{
            display: 'inline-block',
            padding: '20px 36px',
            background: '#818cf80c',
            border: '2px solid #818cf830',
            borderRadius: 20,
          }}
        >
          <div style={{ fontSize: 40, color: '#818cf880', marginBottom: 8, letterSpacing: '0.1em' }}>核心公式</div>
          <div
            style={{
              fontSize: 72,
              fontFamily: '"Times New Roman", Georgia, serif',
              fontStyle: 'italic',
              fontWeight: 700,
              textShadow: '0 0 30px #818cf840',
            }}
          >
            {p.keyFormula}
          </div>
        </div>
      </div>

      {/* Explanation */}
      <div style={{ position: 'absolute', top: 1260, left: 100, right: 60 }}>
        {p.explanation.map((line, i) => {
          const start = expStart + i * 30;
          const opacity = interpolate(frame, [start, start + 20], [0, 1], { extrapolateRight: 'clamp' });
          return (
            <div key={i} style={{ display: 'flex', gap: 10, marginBottom: 14, opacity }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', background: '#818cf8', marginTop: 26, flexShrink: 0, opacity: 0.5 }} />
              <span style={{ fontSize: 40, color: 'rgba(255,255,255,0.7)', lineHeight: 1.5 }}>{line}</span>
            </div>
          );
        })}
      </div>

      {/* Insight */}
      <div style={{ position: 'absolute', bottom: 100, left: 40, right: 40, opacity: insightOpacity, textAlign: 'center' }}>
        <div style={{ display: 'inline-block', padding: '16px 2px', background: '#818cf808', border: '1px solid #818cf820', borderRadius: 50, fontSize: 30, fontWeight: 600 }}>
          💡 {p.insight}
        </div>
      </div>

      {/* Progress */}
      <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${interpolate(frame, [0, 540], [0, 100], { extrapolateRight: 'clamp' })}%`, height: 4, background: 'linear-gradient(90deg, #818cf8, #f472b6, #34d399)' }} />
    </AbsoluteFill>
  );
};
